// SecretsPanel — the app owner's window onto the secrets their app DECLARED (in
// contract.secrets) and where the owner supplies each VALUE. The panel NEVER shows a value
// (the server returns only status); it shows what each secret is FOR, which host it may be
// sent to, whether it's set, and lets the owner set/replace/clear the value. Warm-paper
// styling, matching DataPanel.
//
// The value input is masked (type=password) and validated client-side (validateSecretValue)
// with the SAME rule the server enforces, then sent byte-for-byte (never trimmed).

import { useState } from "react";
import { useAppSecrets } from "../hooks/useAppSecrets";
import { useSignIns, type UseSignIns } from "../hooks/useSignIns";
import { connectWord, providerTitle, signInCallbackUri, signInLabel } from "../lib/sign_in_view";
import { sortSecrets, validateSecretValue, secretStatusLabel } from "../lib/secrets_view";
import type { SecretsEnv } from "../api";
import type { SecretStatus } from "../api";

// Two vaults, one per environment. `dev` is what the app uses while it is being
// built; `prod` is what it will use in front of users — declared ahead from the
// app's contract, so the values can be set BEFORE publishing (the publish gate
// refuses while one is missing). Never copied from one to the other.
export default function SecretsPanel({ slug, initialEnv = "dev" }: { slug: string; initialEnv?: SecretsEnv }) {
  const [env, setEnv] = useState<SecretsEnv>(initialEnv);
  const s = useAppSecrets(slug, env);
  const signIns = useSignIns(slug, env);

  return (
    <div className="flex h-full w-full flex-col bg-bg">
      {/* ONE THIN ROW, not a paragraph.
          Gone: the "Secrets" heading (the tab already says it) and the sentence about values being
          encrypted and never shown — true, and said once in the docs rather than above every visit.
          Gone too: "used while the app is being built" under `dev`, which described the obvious.
          KEPT under `prod`: "set these before publishing" is not a description, it is the one thing
          that bites if nobody reads it. */}
      <header className="flex items-center justify-between gap-3 border-b border-border px-5 py-2">
        <div role="tablist" aria-label="Environment" className="flex min-w-0 items-center gap-1">
          {(["dev", "prod"] as const).map((e) => (
            <button key={e} type="button" role="tab" aria-selected={env === e} onClick={() => setEnv(e)}
              className={["rounded-md px-2.5 py-1 text-[11px] transition", env === e ? "bg-accent text-white" : "text-text-secondary hover:bg-surface-muted hover:text-text"].join(" ")}>
              {e === "dev" ? "dev" : "prod"}
            </button>
          ))}
          {env === "prod" && (
            <span className="truncate pl-2 text-[11px] text-warn dark:text-warn">
              set these before publishing
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <button type="button" onClick={() => { s.reload(); signIns.reload(); }} aria-label="Refresh" title="Refresh" className={ICON_BTN}><RefreshIcon /></button>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
        {s.error
          ? <ErrorNote text={s.error} />
          : s.secrets === null
            ? <Muted>Loading secrets…</Muted>
            : s.secrets.length === 0
              ? <EmptyState />
              // A GRID that fills the panel, not a centred column.
              //
              // It was `mx-auto max-w-2xl flex-col`: one card per row, capped at 42rem, with the rest of
              // a wide panel left empty on both sides. Secrets are a handful of short cards — the cost
              // of that layout was scrolling past emptiness to reach the fourth one.
              //
              // `auto-fill` with a 24rem floor: the browser fits as many columns as the panel allows and
              // one column on a narrow one, with no breakpoint to maintain. The floor is what a card
              // actually needs — its name, a status badge, and "Replace"/"Clear" side by side.
              : <ul className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(24rem,1fr))]">
                  {sortSecrets(s.secrets).map((sec) => (
                    <SecretRow key={sec.name} slug={slug} sec={sec} onSet={s.set} onClear={s.clear} signIns={signIns} />
                  ))}
                </ul>}
      </div>
    </div>
  );
}

function SecretRow({ slug, sec, onSet, onClear, signIns }: {
  slug: string; sec: SecretStatus;
  onSet: (name: string, value: string) => Promise<void>;
  onClear: (name: string) => Promise<void>;
  signIns: UseSignIns;
}) {
  const [editing, setEditing] = useState(false);
  // Connected through PERIMETR's app at the service (platform-oauth): no value of the owner's to set.
  const viaPerimetr = (signIns.byName.get(sec.name)?.provider ?? null) !== null;
  const [value, setValue]     = useState("");
  const [reveal, setReveal]   = useState(false);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const reset = () => { setEditing(false); setValue(""); setReveal(false); setErr(null); setConfirmClear(false); };

  const save = async () => {
    const check = validateSecretValue(value);
    if (!check.ok) { setErr(check.error); return; }
    setBusy(true); setErr(null);
    try { await onSet(sec.name, check.value); reset(); }
    catch (e) { setErr(String((e as Error)?.message ?? e)); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true); setErr(null);
    try { await onClear(sec.name); reset(); }
    catch (e) { setErr(String((e as Error)?.message ?? e)); setConfirmClear(false); }
    finally { setBusy(false); }
  };

  return (
    <li className="rounded-xl border border-border bg-surface px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <code className="font-mono text-sm font-semibold text-text">{sec.name}</code>
            {/* A connection through Perimetr's app (platform-oauth) has no value to set: its badge is the
                connection's, in the block below. */}
            {!viaPerimetr && <StatusBadge isSet={sec.isSet} />}
          </div>
          {sec.purpose && <p className="mt-1 text-xs text-text-secondary">{sec.purpose}</p>}
          <p className="mt-1 text-[11px] text-text-tertiary">
            Sent to <span className="font-mono text-text-secondary">{sec.allowedHosts.join(", ")}</span>
          </p>
        </div>
        {!editing && !viaPerimetr && (
          <div className="flex shrink-0 items-center gap-1.5">
            <button type="button" onClick={() => setEditing(true)} className={sec.isSet ? BTN_GHOST : BTN_PRIMARY}>
              {sec.isSet ? "Replace" : "Set value"}
            </button>
            {sec.isSet && !confirmClear && (
              <button type="button" onClick={() => setConfirmClear(true)} className={BTN_GHOST}>Clear</button>
            )}
          </div>
        )}
      </div>

      {/* Inline confirm for Clear (destructive) */}
      {confirmClear && !editing && (
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-surface-muted px-3 py-2 text-xs">
          <span className="text-text-secondary">Clear this value? The app can’t use it until it’s set again.</span>
          <button type="button" className={BTN_DANGER} onClick={clear} disabled={busy}>{busy ? "Clearing…" : "Clear"}</button>
          <button type="button" className={BTN_GHOST} onClick={() => setConfirmClear(false)} disabled={busy}>Cancel</button>
        </div>
      )}

      {/* Inline value editor (masked) */}
      {editing && (
        <div className="mt-3">
          <div className="flex items-center gap-2">
            {/* A `type=password` field is a PASSWORD to the browser: it saved the
                first secret ever typed here as this site's password and then
                pre-filled it into EVERY secret field — one Save later, the Resend
                key sat in DOG_API_KEY (2026-09-17). `new-password` tells the
                browser this is never a login to fill or to offer saving; the
                data-* attributes say the same to the common password managers.
                A unique name per secret keeps two fields from ever being "the
                same field" to any of them. */}
            <input
              type={reveal ? "text" : "password"}
              name={`secret-${sec.name}`}
              autoComplete="new-password"
              data-1p-ignore data-lpignore="true" data-bwignore="true" data-form-type="other"
              value={value}
              autoFocus
              onChange={(e) => { setValue(e.target.value); if (err) setErr(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") reset(); }}
              placeholder={sec.isSet ? "Enter a new value" : "Paste the secret value"}
              className="min-w-0 flex-1 rounded-lg border border-border bg-bg px-3 py-1.5 font-mono text-xs text-text outline-none focus:border-border-active"
              aria-label={`Value for ${sec.name}`}
            />
            <button type="button" onClick={() => setReveal((r) => !r)} className={ICON_BTN} aria-label={reveal ? "Hide" : "Show"} title={reveal ? "Hide" : "Show"}>
              {reveal ? <EyeOff /> : <Eye />}
            </button>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button type="button" className={BTN_PRIMARY} onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
            <button type="button" className={BTN_GHOST} onClick={reset} disabled={busy}>Cancel</button>
            <span className="text-[11px] text-text-tertiary">Encrypted before it’s stored. Never shown again.</span>
          </div>
        </div>
      )}

      {signIns.byName.has(sec.name) && !editing && <SignInBlock slug={slug} name={sec.name} signIns={signIns} />}

      {err && <p className="mt-2 text-xs text-danger">{err}</p>}
    </li>
  );
}

// A secret the app connects through the service's sign-in window (OAuth2 authorization code): its
// value is the CLIENT secret; the account at the service is connected here. The address to register
// at the service is shown, because the service refuses any other.
function SignInBlock({ slug, name, signIns }: { slug: string; name: string; signIns: UseSignIns }) {
  const st = signIns.byName.get(name)?.status ?? null;
  const provider = signIns.byName.get(name)?.provider ?? null;
  const label = signInLabel(st);
  const uri = signInCallbackUri(window.location.origin, slug, name);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const run = async (f: () => Promise<void>) => {
    setBusy(true); setErr(null);
    try { await f(); } catch (e) { setErr(String((e as Error)?.message ?? e)); } finally { setBusy(false); }
  };
  const copy = () => navigator.clipboard.writeText(uri).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }, () => undefined);
  return (
    <div className="mt-3 rounded-lg bg-surface-muted px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className={["rounded-full px-2 py-px text-[10px] font-semibold uppercase tracking-wide", label.tone === "ok" ? "bg-accent-soft text-accent" : "bg-warn-soft text-warn"].join(" ")}>{label.text}</span>
        <div className="flex shrink-0 items-center gap-1.5">
          <button type="button" className={st === null ? BTN_PRIMARY : BTN_GHOST} disabled={busy} onClick={() => run(() => signIns.connect(name))}>{connectWord(st, provider)}</button>
          {st !== null && <button type="button" className={BTN_GHOST} disabled={busy} onClick={() => run(() => signIns.disconnect(name))}>Disconnect</button>}
        </div>
      </div>
      {provider === null
        ? <>
            <p className="mt-2 text-[11px] text-text-tertiary">Redirect address to register at the service:</p>
            <div className="mt-1 flex items-center gap-1.5">
              <code className="min-w-0 flex-1 truncate font-mono text-[11px] text-text-secondary" title={uri}>{uri}</code>
              <button type="button" className={BTN_GHOST} onClick={copy}>{copied ? "Copied" : "Copy"}</button>
            </div>
          </>
        : <p className="mt-2 text-[11px] text-text-tertiary">Through Perimetr’s app at {providerTitle(provider)} — nothing to set up there: sign in and allow access.</p>}
      {err && <p className="mt-2 text-xs text-danger">{err}</p>}
    </div>
  );
}

function StatusBadge({ isSet }: { isSet: boolean }) {
  const label = secretStatusLabel({ isSet });
  return isSet
    ? <span className="rounded-full bg-accent-soft px-2 py-px text-[10px] font-semibold uppercase tracking-wide text-accent">{label}</span>
    : <span className="rounded-full bg-warn-soft px-2 py-px text-[10px] font-semibold uppercase tracking-wide text-warn">{label}</span>;
}

function EmptyState() {
  return (
    <div className="mx-auto max-w-md py-10 text-center">
      <p className="text-sm font-medium text-text">No secrets declared</p>
      <p className="mt-1.5 text-xs leading-relaxed text-text-secondary">
        When your app needs an API key (Stripe, email, …), it declares it in <code className="font-mono">contract.secrets</code> and deploys. Declared secrets show up here for you to set.
      </p>
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <div className="py-10 text-center text-sm text-text-secondary">{children}</div>;
}
function ErrorNote({ text }: { text: string }) {
  return (
    <div className="mx-auto max-w-md py-10 text-center">
      <p className="text-sm font-medium text-text">Couldn’t load secrets</p>
      <p className="mt-1.5 text-xs leading-relaxed text-text-secondary">{text}</p>
    </div>
  );
}

const ICON_BTN    = "rounded-md p-1.5 text-text-secondary transition hover:bg-surface-muted hover:text-text";
const BTN_PRIMARY = "rounded-full bg-accent px-3 py-1 text-xs font-medium text-white transition hover:bg-accent-hover disabled:opacity-50";
const BTN_GHOST   = "rounded-full border border-border px-3 py-1 text-xs text-text-secondary transition hover:border-border-active hover:text-text disabled:opacity-50";
const BTN_DANGER  = "rounded-full bg-danger-strong px-3 py-1 text-xs font-medium text-white transition hover:bg-danger-hover disabled:opacity-50";

const SVG = (p: { children: React.ReactNode }) => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{p.children}</svg>
);
function RefreshIcon() { return <SVG><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></SVG>; }
function Eye()    { return <SVG><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></SVG>; }
function EyeOff() { return <SVG><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></SVG>; }
